/**
 * Federated `services` payloads built from the **host** `serviceRegistry` only.
 *
 * @remarks
 * **Business Rules:** aga-frontend never calls remote `loadService` / `ActiveRemoteConfig`
 * (that pattern is for titan-shell). This host registers dealcard, funding, collateral, admin,
 * titan, and aga-dashboard services up front and passes the matching subset into each remote via props.
 * Titan’s aga-template `baseService` is the same `ExternalApplicationService` instance as
 * `externalApplicationService`. Dealcard’s `baseService` remains the deals BFF client.
 */
import { adminServiceUtils } from './admin';
import { AUTHENTICATION_SERVICE_NAME } from './authentication';
import { dashboardServiceUtils } from './dashboard';
import { titanServiceUtils } from './titan';

const DASHBOARD_SERVICE_KEYS = [
  'financialAssetsExposureService',
  'obligationScheduleSubmissionsService',
  'chartQueryService',
  'aviationDashboardService',
  'marineDashboardService',
  'railDashboardService',
  'smeDashboardService',
  'nonUsResiDashboardService',
  'nonUsCreDashboardService',
  'tripleNetLeaseDashboardService',
  'reportingObligationsService',
  'dataValidationSummaryService',
  'privateCreditFinanceDashboardService',
  'studentLoanDashboardService',
  'fundFinanceDashboardService',
  'exposureService',
  'ssrmGatewayService',
  'facilityPerformanceService',
  'atlasMappingsService',
  'masterTapeMappingsService',
  'triggersAndCovenantsService',
  'clsDashboardService',
] as const;

type GetServiceFn = <T = unknown>(name: string) => T | undefined;

const ADMIN_SERVICE_KEYS = [
  'adminAuditLogService',
  'adminDomainService',
  'adminFeaturesService',
  'adminPermissionService',
  'adminRolePermissionService',
  'adminRoleService',
  'adminRoleTypeService',
  'adminServiceCatalogService',
  'adminServiceCommunicationService',
] as const;

const DEALCARD_SERVICE_KEYS = [
  'baseService',
  'companyService',
  'dealService',
  'personService',
  'genericService',
  'investorService',
] as const;

const pickFromRegistry = (getService: GetServiceFn, keys: readonly string[]): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const key of keys) {
    out[key] = getService(key);
  }
  return out;
};

const pickDealcardFromRegistry = (getService: GetServiceFn): Record<string, unknown> =>
  pickFromRegistry(getService, DEALCARD_SERVICE_KEYS);

const pickAdminFromRegistry = (getService: GetServiceFn): Record<string, unknown> =>
  pickFromRegistry(getService, ADMIN_SERVICE_KEYS);

/** Services every remote receives, so direct API calls can attach the host session's bearer. */
const pickPlatformFromRegistry = (getService: GetServiceFn): Record<string, unknown> =>
  pickFromRegistry(getService, [AUTHENTICATION_SERVICE_NAME]);

const getRemoteServices = (remote: string, getService: GetServiceFn): Record<string, unknown> => {
  if (remote === 'aga-dealcard') {
    return {
      basePath: '/securitization/dealcard',
      ...pickDealcardFromRegistry(getService),
    };
  }
  if (remote === 'titan') {
    const externalApplicationService = getService('externalApplicationService');
    return {
      basePath: titanServiceUtils.getBasePath(),
      baseService: externalApplicationService,
      fundingKpiService: getService('fundingKpiService'),
      digitalConnectKpiService: getService('digitalConnectKpiService'),
      cncSummaryMetricsService: getService('cncSummaryMetricsService'),
      authorizationUserMetricsService: getService('authorizationUserMetricsService'),
      resiAgaMetricsService: getService('resiAgaMetricsService'),
      externalApplicationService,
    };
  }
  if (remote === 'aga-admin') {
    return {
      basePath: adminServiceUtils.getBasePath(),
      ...pickAdminFromRegistry(getService),
    };
  }
  if (remote === 'aga-dashboard') {
    return {
      basePath: dashboardServiceUtils.getBasePath(),
      ...pickFromRegistry(getService, DASHBOARD_SERVICE_KEYS),
    };
  }
  return {};
};

export const getFederatedServicesForRemote = (remote: string, getService: GetServiceFn): Record<string, unknown> => ({
  ...pickPlatformFromRegistry(getService),
  ...getRemoteServices(remote, getService),
});

export const getLegacyCombinedFederationContext = (getService: GetServiceFn): Record<string, unknown> => ({
  ...pickPlatformFromRegistry(getService),
  basePath: '/securitization/dealcard',
  ...pickDealcardFromRegistry(getService),
  externalApplicationService: getService('externalApplicationService'),
  fundingKpiService: getService('fundingKpiService'),
  digitalConnectKpiService: getService('digitalConnectKpiService'),
  cncSummaryMetricsService: getService('cncSummaryMetricsService'),
  authorizationUserMetricsService: getService('authorizationUserMetricsService'),
  resiAgaMetricsService: getService('resiAgaMetricsService'),
});
